'use client'
import { useDictionary } from '@/contexts/DictionaryContext'
import { motion } from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import { useRef } from 'react'
import { FaEnvelope, FaGithub, FaLinkedin, FaMapMarkerAlt, FaPhone } from 'react-icons/fa'
import { HiChevronUp } from 'react-icons/hi'
import { jsonLdScriptProps } from 'react-schemaorg'
import { Organization, WithContext } from 'schema-dts'

export default function Footer() {
  const { dictionary, locale } = useDictionary()
  const footerRef = useRef<HTMLDivElement>(null)

  const UsefulLinks = [
    { title: dictionary.common.navigation.home, link: `/${locale}` },
    { title: dictionary.common.navigation.about, link: `/${locale}/about-us` },
    { title: dictionary.common.navigation.services, link: `/${locale}/services` },
    { title: dictionary.common.navigation.blog, link: `/${locale}/blog` },
    { title: dictionary.common.navigation.contact, link: `/${locale}/contact` },
  ]

  const ContactInformation = [
    {
      title: dictionary.common.footer.address,
      icon: (
        <FaMapMarkerAlt
          className='text-blue-400'
          aria-hidden='true'
        />
      ),
      content: 'Megapol İzmir, Halkapınar Mahallesi Pakistan Bulvarı, No.19\nBornova / İzmir (Türkiye)',
      link: 'https://maps.app.goo.gl/giGsvMPSB1W1HFjX7',
    },
    {
      title: dictionary.common.footer.phone,
      icon: (
        <FaPhone
          className='text-green-400'
          aria-hidden='true'
        />
      ),
      content: '+90 232 472 34 78',
    },
    {
      title: dictionary.common.footer.email,
      icon: (
        <FaEnvelope
          className='text-amber-400'
          aria-hidden='true'
        />
      ),
      content: 'info@mekanet.com.tr',
    },
  ]

  const Web = ['https://www.mekanetenergy.com', 'https://www.mekanet.com.tr']

  const SocialMedia = [
    {
      title: 'LinkedIn',
      icon: <FaLinkedin aria-hidden='true' />,
      link: 'https://www.linkedin.com/company/mekanet-energy-automation',
      color: 'hover:text-blue-500',
    },
    {
      title: 'GitHub',
      icon: <FaGithub aria-hidden='true' />,
      link: 'https://github.com/Mekanet-Energy',
      color: 'hover:text-gray-400',
    },
  ]

  const certificates = [
    {
      src: '/assets/images/certificates/ehedg_member_logo.png',
      alt: 'EHEDG Member Logo - European Hygienic Engineering & Design Group',
      width: 100,
      height: 100,
      className: 'certificate h-auto w-24 object-contain',
    },
    {
      src: '/assets/images/certificates/iso-14001.png',
      alt: 'ISO 14001:2015 Environmental Management System Certificate',
      width: 100,
      height: 100,
      className: 'iso-certificate h-auto w-24 object-contain',
    },
    {
      src: '/assets/images/certificates/iso-45001.png',
      alt: 'ISO 45001:2018 Occupational Health and Safety Management System Certificate',
      width: 100,
      height: 100,
      className: 'iso-certificate h-auto w-28 object-contain',
    },
    {
      src: '/assets/images/certificates/iso-9001.png',
      alt: 'ISO 9001:2015 Quality Management System Certificate',
      width: 100,
      height: 100,
      className: 'iso-certificate h-auto w-24 object-contain',
    },
    {
      src: '/assets/images/certificates/iso-10002.png',
      alt: 'ISO 10002:2018 Customer Satisfaction Management System Certificate',
      width: 100,
      height: 100,
      className: 'iso-certificate h-auto w-28 object-contain',
    },
    {
      src: '/assets/images/certificates/gcr_cert.png',
      alt: 'GCR Certification for Quality Standards',
      width: 100,
      height: 100,
      className: 'certificate h-auto w-32 object-contain',
    },
  ]

  const Certificates = () => {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        className='flex select-none flex-wrap items-center justify-center gap-8'
      >
        {certificates.map(cert => (
          <motion.div
            key={cert.alt}
            whileHover={{ scale: 1.05 }}
            transition={{ type: 'spring', stiffness: 400, damping: 10 }}
          >
            <Image
              src={cert.src || '/placeholder.svg'}
              alt={cert.alt}
              width={cert.width}
              height={cert.height}
              className={cert.className}
              draggable={false}
            />
          </motion.div>
        ))}
      </motion.div>
    )
  }

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  // Schema.org structured data for Organization
  const organizationSchema: WithContext<Organization> = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Mekanet Energy',
    url: `https://www.mekanet.com.tr/${locale}`,
    logo: {
      '@type': 'ImageObject',
      url: 'https://www.mekanet.com.tr/assets/images/logo/logo.png',
    },
    description: dictionary.common.footer.companyDescription,
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'İzeltaş Galeria Sitesi Karacaoğlan Mahallesi Bornova Caddesi No:7/1I',
      addressLocality: 'Bornova',
      addressRegion: 'İzmir',
      addressCountry: 'TR',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+90 232 472 34 78',
      contactType: 'customer service',
      email: 'info@mekanet.com.tr',
    },
    sameAs: [
      'https://www.linkedin.com/company/mekanet-energy-automation',
      'https://github.com/Mekanet-Energy',
      'http://www.mekanetenergy.com',
      'http://www.mekanet.com.tr',
    ],
  }

  return (
    <>
      <script
        {...jsonLdScriptProps(organizationSchema)}
        type='application/ld+json'
      />
      <footer
        ref={footerRef}
        className='relative bg-linear-to-b from-primary-foreground to-[#0a1929] text-white print:hidden'
        role='contentinfo'
        aria-label='Site footer'
      >
        {/* Decorative Elements */}
        <div
          className='absolute inset-0 overflow-hidden pointer-events-none'
          aria-hidden='true'
        >
          <div className='absolute top-0 left-0 w-full h-px bg-linear-to-r from-transparent via-blue-500/50 to-transparent'></div>
          <div className='absolute -top-8 left-1/4 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl'></div>
          <div className='absolute -top-8 right-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl'></div>
        </div>

        <div className='container relative mx-auto px-4 pt-10 pb-8'>
          {/* Top Section with Logo, About, and Newsletter */}
          <div className='grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 mb-12'>
            {/* Logo and About */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className='space-y-4'
              aria-labelledby='about-section'
            >
              <Link
                href={`/${locale}`}
                className='inline-block'
                aria-label={dictionary.common.logo.srOnly}
              >
                <Image
                  src={'/assets/images/logo/logo.png'}
                  alt={dictionary.common.logo.altText}
                  width={200}
                  height={60}
                  className='h-10 w-auto select-none brightness-0 invert filter'
                  draggable={false}
                  priority
                />
              </Link>
              <div className='mt-4 text-sm text-white max-w-md'>
                <h2
                  id='about-section'
                  className='text-lg font-bold mb-2'
                >
                  {dictionary.common.navigation.about}
                </h2>
                <p className='font-extralight'>{dictionary.common.footer.companyDescription}</p>
              </div>

              {/* Social Media Links */}
              <div className='pt-4'>
                <h3 className='text-sm mb-3'>{dictionary.common.footer.socialMedia}</h3>
                <ul className='flex gap-4'>
                  {SocialMedia.map(social => (
                    <li key={social.title}>
                      <Link
                        href={social.link}
                        className={`text-gray-300 transition-colors ${social.color}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        aria-label={social.title}
                      >
                        {social.icon}
                        <span className='sr-only'>{social.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.section>

            {/* Quick Links */}
            <motion.section
              variants={containerVariants}
              initial='hidden'
              animate='visible'
              className='space-y-6'
              aria-labelledby='links-section'
            >
              <motion.div variants={itemVariants}>
                <h2
                  id='links-section'
                  className='text-lg font-bold mb-4'
                >
                  {dictionary.common.footer.usefulLinks}
                </h2>
                <nav aria-label='Footer Navigation'>
                  <ul className='space-y-2'>
                    {UsefulLinks.map(link => (
                      <li key={link.title}>
                        <Link
                          href={link.link}
                          className='text-sm text-gray-300 hover:text-white hover:underline transition-colors flex items-center w-fit'
                        >
                          {link.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2
                  id='website-links'
                  className='text-lg font-bold mb-4'
                >
                  {dictionary.common.footer.websites}
                </h2>
                <ul
                  className='space-y-2'
                  aria-labelledby='website-links'
                >
                  {Web.map(item => (
                    <li key={item}>
                      <a
                        href={item}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-sm text-gray-300 hover:text-white hover:underline transition-colors'
                      >
                        {item}
                      </a>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.section>

            {/* Contact Section */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              aria-labelledby='contact-section'
            >
              {/* Contact Information */}
              <div>
                <h2
                  id='contact-section'
                  className='text-lg font-bold mb-3'
                >
                  {dictionary.common.footer.contactInfo}
                </h2>
                <address className='not-italic'>
                  <ul className='space-y-3'>
                    {ContactInformation.map(info => (
                      <li
                        key={info.title}
                        className='flex items-start'
                      >
                        <span
                          className='mr-2 mt-1'
                          aria-hidden='true'
                        >
                          {info.icon}
                        </span>
                        <div>
                          <div className='text-xs font-semibold text-gray-400'>{info.title}</div>
                          {info.title === dictionary.common.footer.phone && (
                            <Link
                              href={`tel:${info.content.replace(/\s|$$|$$/g, '')}`}
                              className='text-sm hover:underline'
                            >
                              {info.content}
                            </Link>
                          )}
                          {info.title === dictionary.common.footer.email && (
                            <Link
                              href={`mailto:${info.content}`}
                              className='text-sm hover:underline'
                            >
                              {info.content}
                            </Link>
                          )}
                          {info.title === dictionary.common.footer.address && info.link && (
                            <Link
                              href={info.link}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='text-sm hover:underline'
                            >
                              {info.content}
                            </Link>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </address>
              </div>
            </motion.section>
          </div>

          {/* Certificates Section */}
          <section
            className='border-t border-white/10 pt-8 pb-6'
            aria-labelledby='certificates-section'
          >
            <h2
              id='certificates-section'
              className='text-center text-lg font-bold mb-6'
            >
              {dictionary.home.certifications.title}
            </h2>
            <Certificates />
          </section>

          {/* Bottom Section with Copyright */}
          <div className='border-t border-white/10 pt-6 mt-6 flex flex-col md:flex-row justify-between items-center'>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className='text-sm text-gray-400'
            >
              {dictionary.common.footer.copyright}
            </motion.p>

            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              onClick={scrollToTop}
              className='mt-4 md:mt-0 flex items-center space-x-2 text-sm text-gray-400 hover:text-white transition-colors'
              aria-label={dictionary.common.buttons.backToTop}
            >
              <span>{dictionary.common.buttons.backToTop}</span>
              <HiChevronUp
                className='text-lg'
                aria-hidden='true'
              />
            </motion.button>
          </div>
        </div>
      </footer>
    </>
  )
}
